import React, { type FC, useEffect, useState } from 'react'
import {
  type CommitteeBasicInformation,
  type CommitteeUrl,
  createCommitteeUrl,
  deleteCommitteeUrl,
  updateCommitteeUrl,
  type UsUrlRoleDescType
} from 'services'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Stack,
  Typography,
  Tabs,
  Tab,
  Box,
  Divider
} from '@mui/material'
import { cardStyles, EditableUrlSlot, useUsUrlDescTypeLuts } from 'components'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { getEmptyCommitteeUrl } from 'helpers'
import { URL_DESC_TYPES, URL_DESC_TYPES_REVERSE } from 'utils'

export interface CommitteUrlsCardProps {
  committee: CommitteeBasicInformation
  originalUrls: CommitteeUrl []
  loading: boolean
  updateBasicInfo: React.Dispatch<React.SetStateAction<CommitteeBasicInformation | undefined>>
}

export const CommitteUrlsCard: FC<CommitteUrlsCardProps> = ({
  committee,
  originalUrls,
  loading,
  updateBasicInfo
}) => {
  const [expanded, setExpanded] = useState(true)
  const [selectedTab, setSelectedTab] = useState(0)
  const [currentUrls, setCurrentUrls] = useState<CommitteeUrl[]>(originalUrls)
  const { lutValues: descTypeValues } = useUsUrlDescTypeLuts()

  useEffect(() => {
    setCurrentUrls(originalUrls)
  }, [originalUrls])

  const handleAccordionChange = (): void => {
    setExpanded(!expanded)
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number): void => {
    setSelectedTab(newValue)
  }

  const handleSaved = (saved: CommitteeUrl): void => {
    setCurrentUrls(prev => [
      ...prev.filter(u => u.id !== saved.id && !(u.descId === saved.descId && u.displayOrder === saved.displayOrder)),
      saved
    ])
  }

  const handleRemoved = (removed: CommitteeUrl): void => {
    setCurrentUrls(prev => prev.filter(u => u.id !== removed.id))
  }

  const filteredUrls = currentUrls.filter(url => selectedTab === 0 ? url.displayOrder === 1 : url.displayOrder === 2)

  const getUrl = (descId: number): CommitteeUrl | undefined => {
    return filteredUrls.find(url => url.descId === descId)
  }

  const getDescType = (typeId: number): UsUrlRoleDescType | undefined => {
    return Array.from(descTypeValues.descTypes.values()).find(descType => descType.role.id === committee.role.id &&
        descType.desc.toLowerCase().includes(URL_DESC_TYPES_REVERSE[typeId].toLowerCase())
    )
  }

  const renderUrlRow = (urlType: number): React.ReactNode => {
    const descType = getDescType(urlType)
    if (descType == null) return null
    const typeString = URL_DESC_TYPES_REVERSE[urlType].toLowerCase()
    return (
      <EditableUrlSlot<CommitteeUrl>
        key={`${selectedTab}-${descType.id}`}
        descType={descType}
        url={getUrl(descType.id) ?? null}
        label={`${typeString.charAt(0).toUpperCase()}${typeString.slice(1)}:`}
        createEmptyUrl={() => getEmptyCommitteeUrl(committee.id, selectedTab === 0 ? 1 : 2, descType.type.id, descType.id)}
        onSave={async u => await (u.id <= 0 ? createCommitteeUrl(u) : updateCommitteeUrl(u))}
        onDelete={async u => await deleteCommitteeUrl(u)}
        onSaved={handleSaved}
        onRemoved={handleRemoved}
      />
    )
  }

  return (
    <Card sx={cardStyles}>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
          <div>
            <Typography variant="h6">Urls</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <CardContent>
            <Box mb={3} mt={-5}>
              <Tabs value={selectedTab} onChange={handleTabChange} aria-label="majority-minority-tabs">
              <Tab label="Majority" />
              <Tab label="Minority" />
            </Tabs>
            </Box>
            <Stack spacing={2}>
              {renderUrlRow(URL_DESC_TYPES.EMAIL)}
              {renderUrlRow(URL_DESC_TYPES.WEBSITE)}
              <Box>
                <Divider sx={{ my: 2 }} />
              </Box>
              {renderUrlRow(URL_DESC_TYPES.FACEBOOK)}
              {renderUrlRow(URL_DESC_TYPES.TWITTER)}
              {renderUrlRow(URL_DESC_TYPES.YOUTUBE)}
            </Stack>
          </CardContent>
        </AccordionDetails>
      </Accordion>
    </Card>
  )
}
